/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Warning, LifebuoyIcon, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocPageLayout from "@/components/DocPageLayout";

const CATEGORIES = [
  { value: "bug", label: "Bug / broken feature" },
  { value: "account", label: "Account access / login" },
  { value: "payments", label: "Vendor subscription / billing" },
  { value: "listings", label: "Listing / image / upload" },
  { value: "other", label: "Other technical" },
];

export default function Contact() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("bug");
  const [contactEmail, setContactEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentId, setSentId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) { toast.error("Subject needs at least 3 characters."); return; }
    if (message.trim().length < 10) { toast.error("Message needs at least 10 characters."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", {
        subject: subject.trim(),
        category,
        contact_email: contactEmail.trim() || null,
        message: message.trim(),
      });
      setSentId(data.ticket_id);
      toast.success("Ticket submitted. We'll get back to you as soon as we can.");
      setSubject("");
      setMessage("");
      setContactEmail("");
      setCategory("bug");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSubmitting(false); }
  };

  if (!user) {
    return (
      <DocPageLayout subtitle="Support" title="Contact Us" intro="Please sign in to file a support ticket — we route replies to your account.">
        <div className="flex flex-wrap gap-3">
          <Link to="/login" className="h57-btn-primary" data-testid="contact-signin-cta">Sign In</Link>
          <Link to="/register" className="h57-btn-secondary" data-testid="contact-register-cta">Create Account</Link>
        </div>
      </DocPageLayout>
    );
  }

  return (
    <DocPageLayout
      subtitle="Support"
      title="Contact Us"
      intro="File a support ticket below. We monitor these during business hours and respond via the email attached to your account."
    >
      <div
        data-testid="contact-tech-only-banner"
        className="border border-[#FFD700]/50 bg-[#FFD700]/5 p-4 flex items-start gap-2"
      >
        <Warning size={18} weight="fill" className="text-[#FFD700] shrink-0 mt-0.5" />
        <div className="text-[13px] leading-relaxed text-zinc-200">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFD700] font-bold mb-1">
            Technical issues only
          </div>
          This form is for site problems — bugs, login trouble, upload failures, billing questions. It is
          <span className="text-red-400 font-semibold"> not </span> for transaction disputes, "is this card real?" questions, or seller complaints. Those belong in Messages with the other party — Hunt57 does not arbitrate transactions (see <Link to="/terms" className="text-[#FFD700] underline underline-offset-2 hover:text-white">Terms</Link>).
        </div>
      </div>

      {sentId && (
        <div className="border border-emerald-500/50 bg-emerald-500/10 p-4 text-[13px] text-emerald-300" data-testid="contact-success-banner">
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400 font-bold mb-1">
            Ticket submitted
          </div>
          Ticket ID <span className="font-mono text-white">{sentId}</span> — save this for your records. We'll reply via email.
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        <div>
          <label className="h57-label">Subject</label>
          <input
            className="h57-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="contact-subject"
            placeholder="Short summary — e.g. 'Can't upload back photo on Sell page'"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label className="h57-label">Category</label>
          <select
            className="h57-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="contact-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="h57-label">Reply-to email <span className="text-zinc-500 normal-case tracking-normal">(optional — we default to your account email)</span></label>
          <input
            type="email"
            className="h57-input"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            data-testid="contact-email"
            placeholder={user.email || "you@example.com"}
          />
        </div>

        <div>
          <label className="h57-label">Message</label>
          <textarea
            className="h57-input min-h-[160px]"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="contact-message"
            placeholder="Describe the issue. Include steps to reproduce, what you expected, what happened instead, browser + device, and screenshots if you have them."
            maxLength={5000}
            required
          />
          <div className="mt-1 font-mono text-[10px] text-zinc-500 tracking-widest uppercase text-right tabular-nums">
            {message.length}/5000
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="h57-btn-primary inline-flex items-center gap-2"
          data-testid="contact-submit"
        >
          {submitting ? "Sending…" : (<>Submit Ticket <PaperPlaneTilt size={16} weight="bold" /></>)}
        </button>
      </form>

      <div className="mt-8 border-t border-white/10 pt-6 text-[13px] text-zinc-400">
        <div className="flex items-center gap-2 mb-2 text-white">
          <LifebuoyIcon size={16} weight="fill" className="text-[#FF5F1F]" />
          <span className="font-display font-bold uppercase tracking-tight">Before you submit</span>
        </div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Skim the <Link to="/faq" className="text-[#FF5F1F] underline">FAQ</Link> — 80% of questions are answered there.</li>
          <li>The <Link to="/how-to" className="text-[#FF5F1F] underline">How To</Link> page walks through every user flow.</li>
          <li>For strike or account-standing questions, read <Link to="/trust-safety" className="text-[#FF5F1F] underline">Trust & Safety</Link>.</li>
        </ul>
      </div>
    </DocPageLayout>
  );
}
